import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ContentCpSessionListItem, ContentCardSummary } from '@/lib/api'
import {
  listContentCpSessions,
  createContentCpSession,
  getContentCpSessionWithTurns,
  deleteContentCpSession as apiDeleteContentCpSession,
  updateContentCpSession,
} from '@/lib/api'

console.log('[content-cp-store] Module loading...')

// =============================================================================
// User isolation check - runs SYNCHRONOUSLY before store hydration
// =============================================================================

const CONTENT_CP_USER_ID_KEY = 'content-cp-store-user-id'

function clearStoreIfUserMismatch(): void {
  try {
    const storedUserId = localStorage.getItem(CONTENT_CP_USER_ID_KEY)
    const hasContentCpStore = localStorage.getItem('content-cp-store') !== null

    console.log('[content-cp-store] SYNC: Checking user mismatch')

    let currentUserId: string | null = null

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        try {
          const rawValue = localStorage.getItem(key)
          const authData = JSON.parse(rawValue || '{}')
          currentUserId = authData?.user?.id || authData?.session?.user?.id || null
          if (currentUserId) break
        } catch {
          // Invalid JSON, skip
        }
      }
    }

    if (storedUserId && currentUserId && storedUserId !== currentUserId) {
      console.log('[content-cp-store] SYNC: User mismatch detected! Clearing stale data')
      localStorage.removeItem('content-cp-store')
      localStorage.setItem(CONTENT_CP_USER_ID_KEY, currentUserId)
    } else if (!storedUserId && hasContentCpStore && currentUserId) {
      console.log('[content-cp-store] SYNC: No stored user ID but content-cp-store exists. Clearing orphaned data.')
      localStorage.removeItem('content-cp-store')
      localStorage.setItem(CONTENT_CP_USER_ID_KEY, currentUserId)
    } else if (!storedUserId && currentUserId) {
      console.log('[content-cp-store] SYNC: New user, storing user ID')
      localStorage.setItem(CONTENT_CP_USER_ID_KEY, currentUserId)
    } else if (!currentUserId && hasContentCpStore) {
      console.log('[content-cp-store] SYNC: Clearing orphaned content-cp-store (no logged in user)')
      localStorage.removeItem('content-cp-store')
      localStorage.removeItem(CONTENT_CP_USER_ID_KEY)
    }
  } catch (e) {
    console.error('[content-cp-store] Error checking user mismatch:', e)
  }
}

clearStoreIfUserMismatch()

// =============================================================================
// Types
// =============================================================================

export interface ImageAttachment {
  data: string // base64 data (without data URL prefix)
  mimeType: string
  name: string
}

export interface CardResult {
  card_id: string
  title: string
  type: string
  domain: string
  is_new: boolean
  decision: 'new' | 'duplicate' | 'related'
  similarity?: number
}

export interface CardIngestionSummary {
  source_id: string
  is_duplicate: boolean
  cards_created: number
  cards_duplicate: number
  cards_related: number
  edges_created: number
  cards: CardResult[]
}

// Card ingestion progress tracking
export interface CardIngestionProgress {
  stage: 'idle' | 'ingest' | 'cardify' | 'embed' | 'canonicalize' | 'link' | 'complete' | 'error'
  message: string
  cardsProposed: number
  cardsProcessed: number
  cardsTotal: number
  edgesCreated: number
}

// Workflow queue submission payload
export interface SubmissionPayload {
  id: string
  content: string
  title: string
  tags: string[]
  filename: string | null
  images: ImageAttachment[]
  timestamp: string
}

// Current workflow state
export interface WorkflowState {
  submissionId: string
  progress: CardIngestionProgress
  startedAt: string
}

// Status bar display state
export type StatusBarState =
  | { type: 'idle' }
  | { type: 'processing'; workflow: WorkflowState; queueCount: number }
  | { type: 'success'; cardsCreated: number; edgesCreated: number; fadeAt: number }
  | { type: 'error'; message: string }

// Search mode types
export type SearchMode = 'semantic' | 'keyword' | 'wildcard'
export type SpaceFilter = 'Assets' | 'Operations' | 'Develop' | 'Identity' | 'Entity'

// =============================================================================
// Source Queue Types (Batch Processing)
// =============================================================================

export type SourceType = 'file' | 'text' | 'image'
export type SourceStatus = 'queued' | 'processing' | 'complete' | 'error'

export interface QueuedSource {
  id: string
  type: SourceType

  // File sources
  file?: File
  filename?: string
  fileSize?: number
  mimeType?: string

  // Text sources (pasted content)
  text?: string
  textPreview?: string

  // Image sources (screenshots, photos)
  images?: ImageAttachment[]

  // Metadata
  title?: string
  domainHint?: string

  // Status
  status: SourceStatus
  error?: string

  // Results (after processing)
  sourceId?: string
  cardsCreated?: number
  edgesCreated?: number

  // Timing
  queuedAt: string
  startedAt?: string
  completedAt?: string
}

export interface BatchState {
  isProcessing: boolean
  currentSourceIndex: number
  totalCards: number
  totalEdges: number
  batchStartedAt?: string
  batchCompletedAt?: string
}

// =============================================================================
// Event Log Types
// =============================================================================

export type LogLevel = 'milestone' | 'progress' | 'detail' | 'error'

export interface LogEntry {
  id: string
  ts: string
  elapsed_ms: number
  source_index: number
  type: string
  level: LogLevel
  message: string
  data?: Record<string, unknown>
}

export interface LogState {
  entries: LogEntry[]
  expanded: boolean
  detailLevel: LogLevel
  autoScroll: boolean
}

interface ContentCpState {
  // Session
  sessionId: string | null
  sessionName: string

  // Session list (for sidebar)
  sessions: ContentCpSessionListItem[]
  isLoadingSessions: boolean

  // Session sidebar (left panel)
  showSessionSidebar: boolean

  // Mobile view state
  mobileView: 'sessions' | 'insert' | 'cards'

  // Editor state
  content: string
  title: string
  tags: string[] // Current session's tags (restored from sessionTags on session load)
  filename: string | null
  attachedImages: ImageAttachment[]

  // Per-session persistent data
  sessionTags: Record<string, string[]> // sessionId -> tags
  sessionCardIds: Record<string, string[]> // sessionId -> card IDs (all cards made in session)

  // Workflow queue
  workflowQueue: SubmissionPayload[]
  currentWorkflow: WorkflowState | null
  lastIngestionResult: CardIngestionSummary | null

  // Status bar
  statusBar: StatusBarState
  error: string | null

  // Recent cards from latest ingestion (newest extraction, highlights green)
  recentCardIds: string[]

  // Cards panel state
  showCardsPanel: boolean
  selectedCardId: string | null
  selectedSourceId: string | null
  cardsPanelHistory: Array<{ type: 'card' | 'source'; id: string }>
  selectedCardsTab: 'recent' | 'session' | 'library'

  // Library state (session-scoped cards)
  libraryCards: ContentCardSummary[]
  isLoadingLibrary: boolean

  // Search state (for Library tab)
  searchQuery: string
  searchMode: SearchMode
  filterTags: string[]
  filterSpaces: SpaceFilter[]

  // Source queue (batch processing)
  sourceQueue: QueuedSource[]
  batchState: BatchState

  // Event log
  eventLog: LogState

  // Actions - Session
  setSessionId: (id: string | null) => void
  setSessionName: (name: string) => void
  setSessions: (sessions: ContentCpSessionListItem[]) => void
  setIsLoadingSessions: (loading: boolean) => void
  toggleSessionSidebar: () => void
  closeSessionSidebar: () => void
  setMobileView: (view: 'sessions' | 'insert' | 'cards') => void

  // Session management
  createNewSession: (name?: string) => Promise<void>
  loadSessionFromDb: (sessionId: string) => Promise<void>
  loadSessions: () => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>
  renameSession: (name: string) => Promise<void>
  startNewSession: () => void

  // Editor actions
  setContent: (content: string) => void
  setTitle: (title: string) => void
  setTags: (tags: string[]) => void
  setFilename: (filename: string | null) => void
  setAttachedImages: (images: ImageAttachment[]) => void
  addAttachedImage: (image: ImageAttachment) => void
  removeAttachedImage: (index: number) => void
  clearEditor: () => void

  // Per-session data actions
  saveSessionTags: () => void // Persist current tags to sessionTags[sessionId]
  addSessionCards: (cardIds: string[]) => void // Add cards to current session

  // Workflow queue actions
  queueSubmission: (payload: Omit<SubmissionPayload, 'id' | 'timestamp'>) => string
  startWorkflow: (submissionId: string) => void
  updateWorkflowProgress: (progress: Partial<CardIngestionProgress>) => void
  finishWorkflow: (result: CardIngestionSummary) => void
  failWorkflow: (errorMessage: string) => void
  processNextInQueue: () => SubmissionPayload | null

  // Status bar actions
  setStatusBar: (state: StatusBarState) => void
  clearStatusBar: () => void
  setError: (error: string | null) => void

  // Recent cards (latest ingestion only)
  setRecentCards: (cardIds: string[]) => void // Replace (not append) - latest extraction only
  clearRecentCards: () => void

  // Cards panel
  toggleCardsPanel: () => void
  setSelectedCardId: (id: string | null) => void
  setSelectedSourceId: (id: string | null) => void
  navigateToCard: (id: string) => void
  navigateToSource: (id: string) => void
  navigateBack: () => void
  setSelectedCardsTab: (tab: 'recent' | 'session' | 'library') => void

  // Get session cards for current session
  getSessionCards: () => string[]

  // Library actions (session-scoped cards)
  setLibraryCards: (cards: ContentCardSummary[]) => void
  setIsLoadingLibrary: (loading: boolean) => void

  // Search actions
  setSearchQuery: (query: string) => void
  setSearchMode: (mode: SearchMode) => void
  setFilterTags: (tags: string[]) => void
  toggleFilterTag: (tag: string) => void
  setFilterSpaces: (spaces: SpaceFilter[]) => void
  toggleFilterSpace: (space: SpaceFilter) => void
  clearFilters: () => void

  // Source queue actions (batch processing)
  addSources: (sources: Omit<QueuedSource, 'id' | 'status' | 'queuedAt'>[]) => void
  addFileSource: (file: File) => void
  addTextSource: (text: string, title?: string) => void
  addImageSource: (images: ImageAttachment[], title?: string) => void
  removeSource: (id: string) => void
  clearSources: () => void
  updateSourceStatus: (id: string, status: SourceStatus, result?: Partial<QueuedSource>) => void

  // Batch processing actions
  startBatch: () => void
  cancelBatch: () => void
  completeBatch: () => void
  setCurrentSourceIndex: (index: number) => void

  // Event log actions
  appendLogEntry: (entry: Omit<LogEntry, 'id'>) => void
  clearLog: () => void
  setLogDetailLevel: (level: LogLevel) => void
  toggleLogExpanded: () => void
  setLogAutoScroll: (autoScroll: boolean) => void
}

// =============================================================================
// Store
// =============================================================================

export const useContentCpStore = create<ContentCpState>()(
  persist(
    (set, get) => ({
      // Initial state
      sessionId: null,
      sessionName: 'New Session',
      sessions: [],
      isLoadingSessions: false,
      showSessionSidebar: false,
      mobileView: 'insert',

      // Editor state
      content: '',
      title: '',
      tags: [],
      filename: null,
      attachedImages: [],

      // Per-session persistent data
      sessionTags: {},
      sessionCardIds: {},

      // Workflow queue
      workflowQueue: [],
      currentWorkflow: null,
      lastIngestionResult: null,

      // Status bar
      statusBar: { type: 'idle' },
      error: null,

      // Recent cards (from latest ingestion only)
      recentCardIds: [],

      // Cards panel
      showCardsPanel: true,
      selectedCardId: null,
      selectedSourceId: null,
      cardsPanelHistory: [],
      selectedCardsTab: 'recent',

      // Library state
      libraryCards: [],
      isLoadingLibrary: false,

      // Search state
      searchQuery: '',
      searchMode: 'semantic',
      filterTags: [],
      filterSpaces: [],

      // Source queue (batch processing)
      sourceQueue: [],
      batchState: {
        isProcessing: false,
        currentSourceIndex: -1,
        totalCards: 0,
        totalEdges: 0,
      },

      // Event log
      eventLog: {
        entries: [],
        expanded: true,
        detailLevel: 'progress',
        autoScroll: true,
      },

      // Actions - Session
      setSessionId: (id) => set({ sessionId: id }),
      setSessionName: (name) => set({ sessionName: name }),
      setSessions: (sessions) => set({ sessions }),
      setIsLoadingSessions: (loading) => set({ isLoadingSessions: loading }),
      toggleSessionSidebar: () => set((state) => ({ showSessionSidebar: !state.showSessionSidebar })),
      closeSessionSidebar: () => set({ showSessionSidebar: false }),
      setMobileView: (view) => set({ mobileView: view }),

      // Session management
      createNewSession: async (name?: string) => {
        try {
          // Save current session's tags before switching
          const currentState = get()
          if (currentState.sessionId && currentState.tags.length > 0) {
            set((state) => ({
              sessionTags: {
                ...state.sessionTags,
                [currentState.sessionId!]: currentState.tags,
              },
            }))
          }

          const session = await createContentCpSession(name)
          set({
            sessionId: session.session_id,
            sessionName: session.name || name || 'New Session',
            tags: [], // Reset tags for new session
            recentCardIds: [],
            error: null,
            lastIngestionResult: null,
          })
          // Refresh session list
          get().loadSessions()
        } catch (e) {
          console.error('[content-cp-store] Failed to create session:', e)
          set({ error: 'Failed to create session' })
        }
      },

      loadSessionFromDb: async (sessionId: string) => {
        try {
          // Save current session's tags before switching
          const currentState = get()
          if (currentState.sessionId && currentState.tags.length > 0) {
            set((state) => ({
              sessionTags: {
                ...state.sessionTags,
                [currentState.sessionId!]: currentState.tags,
              },
            }))
          }

          const session = await getContentCpSessionWithTurns(sessionId)
          const state = get()

          // Restore tags for this session from local storage
          const restoredTags = state.sessionTags[sessionId] || []

          set({
            sessionId: session.session_id,
            sessionName: session.name || 'New Session',
            tags: restoredTags,
            recentCardIds: [], // Clear recent (latest ingestion only)
            error: null,
          })
        } catch (e) {
          console.error('[content-cp-store] Failed to load session:', e)
          // Create new session on failure
          get().createNewSession()
        }
      },

      loadSessions: async () => {
        set({ isLoadingSessions: true })
        try {
          const sessions = await listContentCpSessions()
          set({ sessions, isLoadingSessions: false })
        } catch (e) {
          console.error('[content-cp-store] Failed to load sessions:', e)
          set({ isLoadingSessions: false })
        }
      },

      deleteSession: async (sessionId: string) => {
        try {
          await apiDeleteContentCpSession(sessionId)
          // Refresh session list
          get().loadSessions()
          // If deleted current session, create new one
          if (get().sessionId === sessionId) {
            get().createNewSession()
          }
        } catch (e) {
          console.error('[content-cp-store] Failed to delete session:', e)
        }
      },

      renameSession: async (name: string) => {
        const sessionId = get().sessionId
        if (!sessionId) return
        try {
          await updateContentCpSession(sessionId, { name })
          set({ sessionName: name })
          get().loadSessions()
        } catch (e) {
          console.error('[content-cp-store] Failed to rename session:', e)
        }
      },

      startNewSession: () => {
        // Save current session's tags before switching
        const currentState = get()
        if (currentState.sessionId && currentState.tags.length > 0) {
          set((state) => ({
            sessionTags: {
              ...state.sessionTags,
              [currentState.sessionId!]: currentState.tags,
            },
          }))
        }

        set({
          sessionId: null,
          sessionName: 'New Session',
          tags: [],
          recentCardIds: [],
          error: null,
          lastIngestionResult: null,
          content: '',
          title: '',
          filename: null,
          attachedImages: [],
        })
      },

      // Editor actions
      setContent: (content) => set({ content }),
      setTitle: (title) => set({ title }),
      setTags: (tags) => {
        const state = get()
        // Update both current tags and persist to sessionTags
        if (state.sessionId) {
          set({
            tags,
            sessionTags: {
              ...state.sessionTags,
              [state.sessionId]: tags,
            },
          })
        } else {
          set({ tags })
        }
      },
      setFilename: (filename) => set({ filename }),
      setAttachedImages: (images) => set({ attachedImages: images }),
      addAttachedImage: (image) => set((state) => ({
        attachedImages: [...state.attachedImages, image],
      })),
      removeAttachedImage: (index) => set((state) => ({
        attachedImages: state.attachedImages.filter((_, i) => i !== index),
      })),
      clearEditor: () => set({
        content: '',
        title: '',
        filename: null,
        attachedImages: [],
        // Note: tags persist per session
      }),

      // Per-session data actions
      saveSessionTags: () => {
        const state = get()
        if (state.sessionId) {
          set({
            sessionTags: {
              ...state.sessionTags,
              [state.sessionId]: state.tags,
            },
          })
        }
      },

      addSessionCards: (cardIds) => {
        const state = get()
        if (!state.sessionId) return
        const existingCardIds = state.sessionCardIds[state.sessionId] || []
        // Prepend new cards (newest first), avoid duplicates
        const uniqueNewIds = cardIds.filter((id) => !existingCardIds.includes(id))
        set({
          sessionCardIds: {
            ...state.sessionCardIds,
            [state.sessionId]: [...uniqueNewIds, ...existingCardIds],
          },
        })
      },

      // Workflow queue actions
      queueSubmission: (payload) => {
        const id = `sub_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
        const submission: SubmissionPayload = {
          ...payload,
          id,
          timestamp: new Date().toISOString(),
        }
        set((state) => ({
          workflowQueue: [...state.workflowQueue, submission],
        }))
        return id
      },

      startWorkflow: (submissionId) => {
        const state = get()
        // Remove from queue if present (may not be if started immediately)
        set({
          workflowQueue: state.workflowQueue.filter((s) => s.id !== submissionId),
          currentWorkflow: {
            submissionId,
            progress: {
              stage: 'ingest',
              message: 'Starting ingestion...',
              cardsProposed: 0,
              cardsProcessed: 0,
              cardsTotal: 0,
              edgesCreated: 0,
            },
            startedAt: new Date().toISOString(),
          },
          statusBar: {
            type: 'processing',
            workflow: {
              submissionId,
              progress: {
                stage: 'ingest',
                message: 'Starting ingestion...',
                cardsProposed: 0,
                cardsProcessed: 0,
                cardsTotal: 0,
                edgesCreated: 0,
              },
              startedAt: new Date().toISOString(),
            },
            queueCount: state.workflowQueue.length - 1,
          },
        })
      },

      updateWorkflowProgress: (progress) => {
        set((state) => {
          if (!state.currentWorkflow) return state
          const updatedProgress = { ...state.currentWorkflow.progress, ...progress }
          return {
            currentWorkflow: {
              ...state.currentWorkflow,
              progress: updatedProgress,
            },
            statusBar: {
              type: 'processing',
              workflow: {
                ...state.currentWorkflow,
                progress: updatedProgress,
              },
              queueCount: state.workflowQueue.length,
            },
          }
        })
      },

      finishWorkflow: (result) => {
        const state = get()
        const newCardIds = result.cards
          .filter((c) => c.is_new)
          .map((c) => c.card_id)

        // Add to session cards (persistent per session)
        let updatedSessionCardIds = state.sessionCardIds
        if (state.sessionId && newCardIds.length > 0) {
          const existingSessionCards = state.sessionCardIds[state.sessionId] || []
          const uniqueNewIds = newCardIds.filter((id) => !existingSessionCards.includes(id))
          updatedSessionCardIds = {
            ...state.sessionCardIds,
            [state.sessionId]: [...uniqueNewIds, ...existingSessionCards],
          }
        }

        set({
          currentWorkflow: null,
          lastIngestionResult: result,
          // Recent = latest ingestion only (not cumulative)
          recentCardIds: newCardIds,
          // Session cards = all cards from this session (persistent)
          sessionCardIds: updatedSessionCardIds,
          statusBar: {
            type: 'success',
            cardsCreated: result.cards_created,
            edgesCreated: result.edges_created,
            fadeAt: Date.now() + 3000, // Auto-fade after 3 seconds
          },
          // Auto-navigate to source detail
          selectedSourceId: result.source_id,
          selectedCardId: null,
          cardsPanelHistory: [],
        })
      },

      failWorkflow: (errorMessage) => {
        set({
          currentWorkflow: null,
          statusBar: {
            type: 'error',
            message: errorMessage,
          },
          error: errorMessage,
        })
      },

      processNextInQueue: () => {
        const state = get()
        if (state.currentWorkflow) return null // Already processing
        if (state.workflowQueue.length === 0) return null // Nothing queued

        const next = state.workflowQueue[0]
        get().startWorkflow(next.id)
        return next
      },

      // Status bar actions
      setStatusBar: (state) => set({ statusBar: state }),
      clearStatusBar: () => set({ statusBar: { type: 'idle' }, error: null }),
      setError: (error) => set({
        error,
        statusBar: error ? { type: 'error', message: error } : { type: 'idle' },
      }),

      // Recent cards (latest ingestion only)
      setRecentCards: (cardIds) => set({ recentCardIds: cardIds }),
      clearRecentCards: () => set({ recentCardIds: [] }),

      // Cards panel
      toggleCardsPanel: () => set((state) => ({ showCardsPanel: !state.showCardsPanel })),
      setSelectedCardId: (id) => set({ selectedCardId: id, selectedSourceId: null, cardsPanelHistory: [] }),
      setSelectedSourceId: (id) => set({ selectedSourceId: id }),

      navigateToCard: (id) => set((state) => {
        const history = [...state.cardsPanelHistory]
        if (state.selectedSourceId) {
          history.push({ type: 'source', id: state.selectedSourceId })
        } else if (state.selectedCardId) {
          history.push({ type: 'card', id: state.selectedCardId })
        }
        return { selectedCardId: id, selectedSourceId: null, cardsPanelHistory: history }
      }),

      navigateToSource: (id) => set((state) => {
        const history = [...state.cardsPanelHistory]
        if (state.selectedCardId) {
          history.push({ type: 'card', id: state.selectedCardId })
        }
        return { selectedSourceId: id, cardsPanelHistory: history }
      }),

      navigateBack: () => set((state) => {
        const history = [...state.cardsPanelHistory]
        const prev = history.pop()

        if (!prev) {
          return { selectedCardId: null, selectedSourceId: null, cardsPanelHistory: [] }
        }

        if (prev.type === 'card') {
          return { selectedCardId: prev.id, selectedSourceId: null, cardsPanelHistory: history }
        } else {
          return { selectedSourceId: prev.id, cardsPanelHistory: history }
        }
      }),

      setSelectedCardsTab: (tab) => set({
        selectedCardsTab: tab,
        selectedCardId: null,
        selectedSourceId: null,
        cardsPanelHistory: [],
      }),

      // Get session cards for current session
      getSessionCards: () => {
        const state = get()
        if (!state.sessionId) return []
        return state.sessionCardIds[state.sessionId] || []
      },

      // Library actions
      setLibraryCards: (cards) => set({ libraryCards: cards }),
      setIsLoadingLibrary: (loading) => set({ isLoadingLibrary: loading }),

      // Search actions
      setSearchQuery: (query) => set({ searchQuery: query }),
      setSearchMode: (mode) => set({ searchMode: mode }),
      setFilterTags: (tags) => set({ filterTags: tags }),
      toggleFilterTag: (tag) => set((state) => ({
        filterTags: state.filterTags.includes(tag)
          ? state.filterTags.filter((t) => t !== tag)
          : [...state.filterTags, tag],
      })),
      setFilterSpaces: (spaces) => set({ filterSpaces: spaces }),
      toggleFilterSpace: (space) => set((state) => ({
        filterSpaces: state.filterSpaces.includes(space)
          ? state.filterSpaces.filter((s) => s !== space)
          : [...state.filterSpaces, space],
      })),
      clearFilters: () => set({
        searchQuery: '',
        filterTags: [],
        filterSpaces: [],
      }),

      // Source queue actions (batch processing)
      addSources: (sources) => set((state) => ({
        sourceQueue: [
          ...state.sourceQueue,
          ...sources.map((s) => ({
            ...s,
            id: `src_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            status: 'queued' as SourceStatus,
            queuedAt: new Date().toISOString(),
          })),
        ],
      })),

      addFileSource: (file) => {
        const isImage = file.type.startsWith('image/')
        const sourceType: SourceType = isImage ? 'image' : 'file'

        if (isImage) {
          // Read image as base64
          const reader = new FileReader()
          reader.onload = () => {
            const dataUrl = reader.result as string
            const base64Data = dataUrl.split(',')[1]
            set((state) => ({
              sourceQueue: [
                ...state.sourceQueue,
                {
                  id: `src_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                  type: 'image',
                  filename: file.name,
                  fileSize: file.size,
                  mimeType: file.type,
                  images: [{
                    data: base64Data,
                    mimeType: file.type,
                    name: file.name,
                  }],
                  status: 'queued',
                  queuedAt: new Date().toISOString(),
                },
              ],
            }))
          }
          reader.readAsDataURL(file)
        } else {
          // Store file reference directly
          set((state) => ({
            sourceQueue: [
              ...state.sourceQueue,
              {
                id: `src_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                type: sourceType,
                file,
                filename: file.name,
                fileSize: file.size,
                mimeType: file.type,
                status: 'queued',
                queuedAt: new Date().toISOString(),
              },
            ],
          }))
        }
      },

      addTextSource: (text, title) => set((state) => ({
        sourceQueue: [
          ...state.sourceQueue,
          {
            id: `src_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            type: 'text',
            text,
            textPreview: text.slice(0, 100),
            title,
            status: 'queued',
            queuedAt: new Date().toISOString(),
          },
        ],
      })),

      addImageSource: (images, title) => set((state) => ({
        sourceQueue: [
          ...state.sourceQueue,
          {
            id: `src_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            type: 'image',
            images,
            title,
            filename: images[0]?.name || 'pasted-image',
            status: 'queued',
            queuedAt: new Date().toISOString(),
          },
        ],
      })),

      removeSource: (id) => set((state) => ({
        sourceQueue: state.sourceQueue.filter((s) => s.id !== id),
      })),

      clearSources: () => set({
        sourceQueue: [],
        batchState: {
          isProcessing: false,
          currentSourceIndex: -1,
          totalCards: 0,
          totalEdges: 0,
        },
      }),

      updateSourceStatus: (id, status, result) => set((state) => ({
        sourceQueue: state.sourceQueue.map((s) =>
          s.id === id ? { ...s, status, ...result } : s
        ),
      })),

      // Batch processing actions
      startBatch: () => set((state) => ({
        batchState: {
          isProcessing: true,
          currentSourceIndex: 0,
          totalCards: 0,
          totalEdges: 0,
          batchStartedAt: new Date().toISOString(),
        },
        eventLog: {
          ...state.eventLog,
          entries: [], // Clear log on new batch
        },
      })),

      cancelBatch: () => set((state) => ({
        batchState: {
          ...state.batchState,
          isProcessing: false,
        },
        sourceQueue: state.sourceQueue.map((s) =>
          s.status === 'processing' ? { ...s, status: 'queued' } : s
        ),
      })),

      completeBatch: () => set((state) => ({
        batchState: {
          ...state.batchState,
          isProcessing: false,
          currentSourceIndex: -1,
          batchCompletedAt: new Date().toISOString(),
        },
      })),

      setCurrentSourceIndex: (index) => set((state) => ({
        batchState: {
          ...state.batchState,
          currentSourceIndex: index,
        },
      })),

      // Event log actions
      appendLogEntry: (entry) => set((state) => ({
        eventLog: {
          ...state.eventLog,
          entries: [
            ...state.eventLog.entries,
            {
              ...entry,
              id: `log_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            },
          ],
        },
      })),

      clearLog: () => set((state) => ({
        eventLog: {
          ...state.eventLog,
          entries: [],
        },
      })),

      setLogDetailLevel: (level) => set((state) => ({
        eventLog: {
          ...state.eventLog,
          detailLevel: level,
        },
      })),

      toggleLogExpanded: () => set((state) => ({
        eventLog: {
          ...state.eventLog,
          expanded: !state.eventLog.expanded,
        },
      })),

      setLogAutoScroll: (autoScroll) => set((state) => ({
        eventLog: {
          ...state.eventLog,
          autoScroll,
        },
      })),
    }),
    {
      name: 'content-cp-store',
      partialize: (state) => ({
        // Only persist these fields
        sessionId: state.sessionId,
        sessionName: state.sessionName,
        tags: state.tags, // Current session's tags
        sessionTags: state.sessionTags, // All sessions' tags (per-session persistence)
        sessionCardIds: state.sessionCardIds, // All sessions' cards (per-session persistence)
        showCardsPanel: state.showCardsPanel,
        selectedCardsTab: state.selectedCardsTab,
        // Note: recentCardIds is NOT persisted (only shows latest extraction)
      }),
    }
  )
)
